import { useEffect, useRef } from 'react';

const CURRENT_BUILD_ID = String(process.env.REACT_APP_BUILD_ID || '').trim();
const POLL_MS = 60 * 1000;

async function clearSiteCaches() {
  if (!('caches' in window)) {
    return;
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch (e) {
    // ignore
  }
}

async function fetchRemoteBuildId() {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  const id = data && data.buildId;
  return id ? String(id).trim() : null;
}

/**
 * When a new deploy is live, reload clients onto the latest build.
 * Relies on REACT_APP_BUILD_ID (baked at build) vs /version.json (served by nginx).
 */
export default function AppVersionWatcher() {
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!CURRENT_BUILD_ID || process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    const applyUpdate = async (remoteId) => {
      if (reloadingRef.current || !remoteId || remoteId === CURRENT_BUILD_ID) {
        return;
      }
      reloadingRef.current = true;
      await clearSiteCaches();
      // Hard navigation so browsers re-fetch index.html + new hashed assets.
      window.location.reload();
    };

    const check = async () => {
      try {
        const remoteId = await fetchRemoteBuildId();
        await applyUpdate(remoteId);
      } catch (e) {
        // offline / transient — ignore
      }
    };

    check();
    const timer = window.setInterval(check, POLL_MS);

    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return null;
}
